import { cn, describeVisibility } from "@vellum/ui";
import { type LinkSchema } from "@vellum/vellum-api";
import type { ReactNode } from "react";

export const CARD_CLASSES =
  "relative flex min-h-42 min-w-40 flex-col overflow-hidden rounded-2xl border bg-white p-3 lg:p-5 shadow-sm text-left";

/** Light tile: links. Folders use `FOLDER_ICON_TILE_CLASSES`. */
const ICON_TILE_CLASSES =
  "flex h-8 md:h-10 w-8 md:w-10 items-center justify-center rounded-xl bg-green-300/20 text-green-700 ring-1 ring-green-700/10";

/** Dark tile, so a folder reads differently from a link by lightness, not hue. */
export const FOLDER_ICON_TILE_CLASSES =
  "flex h-8 md:h-10 w-8 md:w-10 items-center justify-center rounded-xl bg-green-700 text-white ring-1 ring-green-700/10";

/** Hover treatment shared by the clickable (non-edit) link and folder cards. */
export const INTERACTIVE_CARD_CLASSES =
  "group border-green-700/10 no-underline transition-all duration-200 hover:-translate-y-0.5 hover:border-green-300/70 hover:shadow-lg hover:shadow-green-700/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500";

export function CardBody({
  title,
  description,
}: {
  title: string;
  description?: string | null;
}) {
  return (
    <>
      <h3 className="mt-4 mb-1 text-sm md:text-base font-semibold tracking-tight text-green-700">
        {title}
      </h3>
      <p className="m-0 flex-1 text-xs md:text-sm leading-snug text-gray-600">
        {description}
      </p>
    </>
  );
}

export function CardIcon({
  icon,
  fallback,
  tileClassName,
}: {
  icon?: string | null;
  fallback: string;
  tileClassName: string;
}) {
  return (
    <span className={tileClassName}>
      <span
        className={`iconoir-${icon ?? fallback} text-lg md:text-xl`}
        aria-hidden="true"
      />
    </span>
  );
}

export function LinkCard({ link }: { link: LinkSchema }) {
  return (
    <a
      href={link.url}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(CARD_CLASSES, INTERACTIVE_CARD_CLASSES)}
    >
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-0.5 bg-linear-to-r from-green-300/0 via-green-300 to-green-300/0 opacity-0 transition-opacity duration-200 group-hover:opacity-100"
      />

      <div className="flex items-start justify-between">
        <CardIcon
          icon={link.icon}
          fallback="app-window"
          tileClassName={cn(
            ICON_TILE_CLASSES,
            "transition-colors duration-200 group-hover:bg-green-700 group-hover:text-neutral-500",
          )}
        />
        <span
          aria-hidden="true"
          className="iconoir-arrow-up-right text-lg text-gray-300 transition-all duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-green-700"
        />
      </div>

      <CardBody title={link.title} description={link.description} />
    </a>
  );
}

export interface EditableCardProps {
  title: string;
  description?: string | null;
  visibleToRoles: string[];
  onEdit: () => void;
  onDelete: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDropOn: () => void;
  dragging: boolean;
}

/**
 * The manage-mode frame shared by links and folders: edit, delete, a
 * visibility summary (admins see every item, including restricted ones), and
 * drag handles for reordering. `icon` and `children` fill in the specifics.
 */
export function EditableCard({
  title,
  description,
  visibleToRoles,
  onEdit,
  onDelete,
  onDragStart,
  onDragEnd,
  onDropOn,
  dragging,
  icon,
  children,
}: EditableCardProps & { icon: ReactNode; children?: ReactNode }) {
  return (
    <div
      draggable
      onDragStart={(event) => {
        // Nested drag targets (a folder dialog over the grid) must not also
        // start a drag on whatever sits behind them.
        event.stopPropagation();
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onDropOn();
      }}
      className={cn(
        CARD_CLASSES,
        "cursor-grab border-dashed border-green-700/20 active:cursor-grabbing",
        dragging && "opacity-40",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        {icon}
        <div className="flex items-center gap-1">
          <span
            className="iconoir-drag flex h-7 w-7 pt-1 items-center justify-center text-lg text-gray-300"
            aria-hidden="true"
            title="Drag to reorder"
          />
          <button
            type="button"
            aria-label={`Edit ${title}`}
            onClick={onEdit}
            className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-blue-500/10 hover:text-blue-500"
          >
            <span className="iconoir-edit-pencil text-lg" aria-hidden="true" />
          </button>
          <button
            type="button"
            aria-label={`Delete ${title}`}
            onClick={onDelete}
            className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-error/10 hover:text-error"
          >
            <span className="iconoir-trash text-lg" aria-hidden="true" />
          </button>
        </div>
      </div>
      <CardBody title={title} description={description} />
      {children}
      <span className="mt-3 text-xs text-gray-500">
        Visible to:{" "}
        <span className="font-semibold">
          {describeVisibility(visibleToRoles)}
        </span>
      </span>
    </div>
  );
}

type EditableLinkCardProps = Omit<
  EditableCardProps,
  "title" | "description" | "visibleToRoles"
> & { link: LinkSchema };

export function EditableLinkCard({ link, ...props }: EditableLinkCardProps) {
  return (
    <EditableCard
      {...props}
      title={link.title}
      description={link.description}
      visibleToRoles={link.visible_to_roles ?? []}
      icon={
        <CardIcon
          icon={link.icon}
          fallback="app-window"
          tileClassName={ICON_TILE_CLASSES}
        />
      }
    />
  );
}

interface AddCardProps {
  label: string;
  icon: string;
  onClick: () => void;
}

export function AddCard({ label, icon, onClick }: AddCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex min-h-42 flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 bg-white/60 p-5 text-gray-500 hover:border-blue-500 hover:text-blue-500"
    >
      <span className={`iconoir-${icon} text-2xl`} aria-hidden="true" />
      <span className="text-sm font-semibold">{label}</span>
    </button>
  );
}
